import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ApprovalRequest, AttachmentInfo } from "../types/legalOps";
import { 
  createApprovalRequest, 
  listApprovalRequestsByModule, 
  updateApprovalRequest,
  deleteApprovalRequest,
  uploadApprovalFile 
} from "../services/approvalRequestsService";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useToast } from "../components/Toast";
import { db, serverTimestamp } from "../../firebaseClient";
import { uploadGovCorrespondenceFiles } from "../services/storageUpload";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate } from "../utils/dateUtils";
import { useAiContext } from "../ai/AiContext";
import { createLegalActivity } from "../services/legalActivityService";
import { canEditModule } from "../config/roles";
import { generateDocCode } from "../utils/idGenerator";
import { DEPARTMENT_OPTIONS } from "../constants/departments";

// New Approval System
import { updateStageDecision } from "../services/approvalFlowService";
import { useApprovalFlow } from "../hooks/useApprovalFlow";
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { createOrResetApprovalFlow } from "../services/approvalFlowService";

export function GovCorrespondencePage() {
  const [activeTab, setActiveTab] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="row" style={{ borderBottom: '1px solid #333', marginBottom: 16, flexShrink: 0 }}>
        <button 
          className="navbtn" 
          style={{ width: 'auto', borderBottom: activeTab === 'INBOUND' ? '2px solid #fff' : 'none', borderRadius: 0, color: activeTab === 'INBOUND' ? '#fff' : 'var(--muted)' }}
          onClick={() => setActiveTab('INBOUND')}
        >
          公文來文 (Inbound)
        </button>
        <button 
          className="navbtn" 
          style={{ width: 'auto', borderBottom: activeTab === 'OUTBOUND' ? '2px solid #fff' : 'none', borderRadius: 0, color: activeTab === 'OUTBOUND' ? '#fff' : 'var(--muted)' }}
          onClick={() => setActiveTab('OUTBOUND')}
        >
          公文函覆 (Outbound)
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display:'flex', flexDirection:'column' }}>
        {activeTab === 'INBOUND' ? <InboundTab /> : <OutboundTab />}
      </div>
    </div>
  );
}

function InboundTab() {
  const { user } = useAuth();
  const canEdit = user ? canEditModule(user.role, 'GOV_CORRESPONDENCE') : false;

  const [items, setItems] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  // internalDocNo is auto generated, officialDocNo is manual
  const [form, setForm] = useState({ 
      id: '', internalDocNo: '', officialDocNo: '', 
      date: '', agency: '', subject: '', dept: '', owner: '', desc: '' 
  });
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<Error|null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const { requestDelete } = useAiContext();
  const { showToast } = useToast();

  useEffect(() => {
    const q = db.collection("gov_inbound_docs").orderBy("createdAt", "desc");
    return q.onSnapshot(snap => {
        setItems(snap.docs.map(d => ({id: d.id, ...d.data()})));
        setError(null);
    }, err => setError(err));
  }, []);

  const handleOpenCreate = () => {
      setEditingItem(null);
      // Auto Gen Internal No: IN-YYYYMMDD-XXX
      const newCode = generateDocCode("gov_in", new Date(), items);
      setForm({ 
          id: '', internalDocNo: newCode, officialDocNo: '', 
          date: formatDate(new Date()), agency: '', subject: '', dept: '法務部', owner: user?.displayName||'', desc: '' 
      });
      setShowModal(true);
  };

  const handleEdit = (item: any) => {
      setEditingItem(item);
      setForm({ 
          id: item.id, 
          internalDocNo: item.internalDocNo || item.docNo || '', // fallback
          officialDocNo: item.officialDocNo || '',
          date: item.date, 
          agency: item.agency, 
          subject: item.subject, 
          dept: item.dept || '', 
          owner: item.owner || '', 
          desc: item.desc || '' 
      });
      setShowModal(true);
  };

  const handleDelete = (item: any) => {
      requestDelete({
          targetType: 'inboundDoc',
          targetId: item.id,
          summary: `公文來文：${item.subject}`,
          onConfirm: async () => {
              try {
                  await db.collection("gov_inbound_docs").doc(item.id).delete();
                  showToast("已刪除來文");
              } catch(e) { showToast("刪除失敗", "error"); }
          }
      });
  };

  const handleSave = async () => {
      if(!form.subject) return alert("請填寫主旨");
      let docId = editingItem?.id;
      let isNew = false;
      const payload = {
          internalDocNo: form.internalDocNo,
          officialDocNo: form.officialDocNo,
          docNo: form.internalDocNo, // Keep legacy field sync for now
          date: form.date,
          agency: form.agency,
          subject: form.subject,
          dept: form.dept,
          owner: form.owner,
          desc: form.desc,
          type: "INBOUND",
          updatedAt: serverTimestamp()
      };

      if (docId) {
          await db.collection("gov_inbound_docs").doc(docId).update(payload);
      } else {
          isNew = true;
          const ref = await db.collection("gov_inbound_docs").add({
              ...payload,
              status: "RECEIVED",
              createdAt: serverTimestamp()
          });
          docId = ref.id;
      }

      if (files.length > 0 && docId) {
          const uploaded = await uploadGovCorrespondenceFiles(docId, files);
          await db.collection("gov_inbound_docs").doc(docId).update({ attachments: uploaded });
      }

      showToast(isNew ? "已新增來文" : "已更新來文");
      setShowModal(false);
      setFiles([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flexShrink: 0 }}>
        <DataToolbar canEdit={canEdit} onCreate={handleOpenCreate} onExport={()=>{}} title="公文來文列表" />
        {error && <div className="err" style={{marginBottom:16}}>讀取失敗: {error.message}</div>}
      </div>
      <div style={{ padding: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", background: "#161616", border: "1px solid var(--border)", borderRadius: 16 }}>
        <div style={{ overflowY: 'auto', padding: 10 }}>
           <div style={{display:'grid', gap:10}}>
              {items.map(item => (
                 <div key={item.id} className="tile" style={{display:'flex', justifyContent:'space-between', alignItems: 'center'}}>
                    <div>
                       <div style={{fontWeight:'bold', fontSize:16, color:'#fff'}}>
                           <span style={{color: '#fbbf24', marginRight: 8, fontSize: 14, fontFamily:'monospace'}}>{item.internalDocNo}</span>
                           {item.subject}
                       </div>
                       <div style={{fontSize:13, color:'var(--muted)', marginTop: 4}}>
                          公文文號: {item.officialDocNo || '-'} | 機關: {item.agency} | 收文日: {item.date}
                       </div>
                       <div style={{fontSize:12, marginTop:4, color: '#aaa'}}>負責人: {item.owner || '-'}</div>
                    </div>
                    <ActionButtons canEdit={canEdit} onPreview={() => handleEdit(item)} onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item)} />
                 </div>
              ))}
              {!error && items.length === 0 && <div className="p" style={{textAlign:'center', padding:20}}>尚無來文資料</div>}
           </div>
        </div>
      </div>
      {showModal && (
        <Modal title={editingItem ? "編輯公文來文" : "新增公文來文"} onClose={() => setShowModal(false)} width={600}>
           <div className="grid">
              <div className="field"><label className="label">內部編號 (自動)</label><input className="input" value={form.internalDocNo} readOnly style={{background:'rgba(255,255,255,0.05)', color:'#fbbf24'}} /></div>
              <div className="field"><label className="label">正式公文文號 (手動)</label><input className="input" value={form.officialDocNo} onChange={e=>setForm({...form, officialDocNo:e.target.value})} placeholder="請輸入來文機關之文號" /></div>
              <DateField label="來文日期" value={parseDate(form.date)} onChange={d=>setForm({...form, date:formatDate(d)})} />
              <div className="field"><label className="label">來文機關</label><input className="input" value={form.agency} onChange={e=>setForm({...form, agency:e.target.value})} /></div>
              <div className="field"><label className="label">來文主旨</label><input className="input" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})} /></div>
              <div className="field"><label className="label">負責部門</label><input className="input" value={form.dept} onChange={e=>setForm({...form, dept:e.target.value})} /></div>
              <div className="field"><label className="label">負責人</label><input className="input" value={form.owner} onChange={e=>setForm({...form, owner:e.target.value})} /></div>
              <div className="field" style={{gridColumn:'1/-1'}}><label className="label">說明/擬辦</label><textarea className="input" rows={3} value={form.desc} onChange={e=>setForm({...form, desc:e.target.value})} /></div>
           </div>
           <FileUploadKit existingFiles={[]} onFilesSelected={fs => setFiles(p => [...p, ...fs])} />
           <div className="row" style={{marginTop:20, justifyContent:'flex-end'}}><button className="btn" onClick={handleSave}>儲存</button></div>
        </Modal>
      )}
    </div>
  );
}

function OutboundTab() {
  const { user } = useAuth();
  const canEdit = user ? canEditModule(user.role, 'GOV_CORRESPONDENCE') : false;

  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [inbounds, setInbounds] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState<ApprovalRequest|null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error|null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // New Fields
  const [form, setForm] = useState({ 
      title: '', agency: '', desc: '', inboundId: '', inboundDocNo: '',
      internalDocNo: '', officialDocNo: '', // New ID fields
      date: new Date().toISOString().split('T')[0],
      urgency: 'NORMAL', isSecret: false,
      mainDepartment: '法務部', 
      handlerName: user?.displayName || '',
      replyToInternalDocNo: '', // Linking
      reviewAttachments: [] as AttachmentInfo[],
      kcAttachments: [] as AttachmentInfo[]
  });
  const [files, setFiles] = useState<File[]>([]);
  const { showToast } = useToast();
  const { requestDelete } = useAiContext();

  // Approval Flow Hook
  const { flow, loading: flowLoading } = useApprovalFlow("OFFICIAL_REPLY", selectedReq?.id || "");

  useEffect(() => {
    const unsub = listApprovalRequestsByModule('govLetter', setItems, setError);
    db.collection("gov_inbound_docs").orderBy("createdAt", "desc").limit(50).get().then(snap => {
        setInbounds(snap.docs.map(d => ({id: d.id, ...d.data()})));
    }).catch(err => console.error("Inbound fetch error", err));
    return () => { unsub.then(fn => fn()); };
  }, []);

  const handleOpenCreate = () => {
      setEditingId(null);
      const code = generateDocCode("gov_out", new Date(), items);
      setForm({ 
          title: '', agency: '', desc: '', inboundId: '', inboundDocNo: '',
          internalDocNo: code, officialDocNo: '',
          date: new Date().toISOString().split('T')[0],
          urgency: 'NORMAL', isSecret: false,
          mainDepartment: '法務部', handlerName: user?.displayName || '',
          replyToInternalDocNo: '',
          reviewAttachments: [],
          kcAttachments: []
      });
      setFiles([]);
      setShowModal(true);
  };

  const handleEdit = (item: ApprovalRequest) => {
      setEditingId(item.id);
      setForm({
          title: item.title,
          agency: item.counterparty || '',
          desc: item.description,
          inboundId: '', inboundDocNo: '', 
          internalDocNo: item.docCode || '',
          officialDocNo: (item as any).officialDocNo || '',
          date: item.submittedAt?.toDate ? formatDate(item.submittedAt.toDate()) : '',
          urgency: item.priority === 'urgent' ? 'URGENT' : 'NORMAL',
          isSecret: item.sensitivity === 'confidential',
          mainDepartment: (item as any).mainDepartment || '法務部',
          handlerName: (item as any).handlerName || item.requesterName,
          replyToInternalDocNo: (item as any).replyToInternalDocNo || '',
          reviewAttachments: item.reviewAttachments || [],
          kcAttachments: item.kcAttachments || []
      });
      setFiles([]); 
      setShowModal(true);
  };

  const handleDelete = (item: ApprovalRequest) => {
      requestDelete({
          targetType: 'govLetter',
          targetId: item.id,
          summary: `公文函覆：${item.title}`,
          onConfirm: async () => {
              try {
                  await deleteApprovalRequest(item.id);
                  showToast("已刪除函覆");
              } catch(e) { showToast("刪除失敗", "error"); }
          }
      });
  };

  const handleSave = async (startApproval = false) => {
    if(!form.title || !form.agency || !form.internalDocNo || !form.mainDepartment || !form.handlerName) return alert("請填寫所有必填欄位");
    setIsSubmitting(true);
    try {
        const payload = {
            module: 'govLetter' as const,
            title: form.title, 
            counterparty: form.agency, 
            department: form.mainDepartment,
            requesterUid: user?.id||'',
            requesterName: form.handlerName, 
            docCode: form.internalDocNo, // Map internal ID to docCode
            priority: (form.urgency === 'URGENT' ? 'urgent' : 'normal') as any, 
            sensitivity: (form.isSecret ? 'confidential' : 'normal') as any,
            description: `函覆內容摘要：\n${form.desc}\n\n關聯來文：${form.replyToInternalDocNo || '無'}`,
            status: 'DRAFT' as any,
            // Extra fields
            officialDocNo: form.officialDocNo,
            replyToInternalDocNo: form.replyToInternalDocNo,
            mainDepartment: form.mainDepartment,
            handlerName: form.handlerName
        };

        let docId = editingId;
        if (editingId) {
            await updateApprovalRequest(editingId, payload);
            showToast("已更新");
        } else {
            docId = await createApprovalRequest({ ...payload, attachments: [] });
            showToast("已建立");
        }

        if (files.length > 0 && docId) {
            const atts: AttachmentInfo[] = [];
            for (const f of files) {
                const meta = await uploadApprovalFile(docId, 'request', f, user?.email || '');
                atts.push(meta);
            }
            await updateApprovalRequest(docId, { attachments: atts });
        }

        if (startApproval && docId) {
            await createOrResetApprovalFlow("OFFICIAL_REPLY", docId, user?.id || "");
            await updateApprovalRequest(docId, { status: "SUBMITTED" });
            showToast("已送出簽核");
        }
        
        setShowModal(false);
    } catch(e) {
        console.error(e);
        showToast("操作失敗", "error");
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleDecision = async (
    stageKey: string,
    decision: "APPROVED" | "REJECTED" | "DONE" | "RETURNED",
    comment: string,
    deptCode?: string,
  ) => {
      void deptCode;
      if (decision === "DONE") return;

      if (!flow) return;
      if (!comment) return alert("請填寫意見");
      try {
          // Cast explicitly to satisfy the type requirement
          const safeDecision = decision as "APPROVED" | "REJECTED" | "RETURNED" | "DONE";
          await updateStageDecision({
              flowId: flow.id,
              stageKey: stageKey as any,
              decision: safeDecision,
              comment,
              actorUid: user?.id || 'unknown',
              actorName: user?.displayName || 'User',
              deptCode
          });
          
          let newStatus = selectedReq!.status;
          
          if (decision === 'REJECTED') {
              newStatus = 'REJECTED';
          } else if (decision === 'RETURNED') {
              newStatus = 'LEGAL_RETURNED';
          } else {
              if (stageKey === 'LEGAL_REVIEW') {
                  newStatus = 'WAITING_APPROVAL'; 
              } else if (stageKey === 'MANAGER_IRENE') {
                  newStatus = 'FULLY_APPROVED';
              }
          }
          
          await updateApprovalRequest(selectedReq!.id, { status: newStatus as any });
          showToast("審核完成");
      } catch(e) {
          console.error(e);
          showToast("操作失敗", "error");
      }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flexShrink: 0 }}>
        <DataToolbar canEdit={canEdit} onCreate={handleOpenCreate} onExport={()=>{}} title="公文函覆簽核" />
        {error && <div className="err">讀取失敗: {error.message}</div>}
      </div>

      <div style={{ padding: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", background: "#161616", border: "1px solid var(--border)", borderRadius: 16 }}>
         <div style={{ overflowY: 'auto', flex: 1 }}>
           <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#222', color: '#aaa', textAlign: 'left', position: 'sticky', top: 0 }}>
                 <tr>
                    <th style={{padding:12}}>內部編號</th>
                    <th style={{padding:12}}>發文文號</th>
                    <th style={{padding:12}}>主旨</th>
                    <th style={{padding:12}}>受文機關</th>
                    <th style={{padding:12}}>主責部門</th>
                    <th style={{padding:12}}>狀態</th>
                    <th style={{padding:12, textAlign: 'right'}}>操作</th>
                 </tr>
              </thead>
              <tbody>
                 {items.map(item => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #333' }}>
                       <td style={{padding:12, fontFamily:'monospace', color:'var(--muted)'}}>{item.docCode || '-'}</td>
                       <td style={{padding:12}}>{(item as any).officialDocNo || '-'}</td>
                       <td style={{padding:12, fontWeight:500, color:'#fff'}}>{item.title}</td>
                       <td style={{padding:12}}>{item.counterparty}</td>
                       <td style={{padding:12}}>{(item as any).mainDepartment || item.department}</td>
                       <td style={{padding:12}}>
                          <span className="badge">{item.status}</span>
                       </td>
                       <td style={{padding:12, textAlign: 'right'}}>
                          <ActionButtons 
                            canEdit={canEdit}
                            onPreview={() => setSelectedReq(item)}
                            onEdit={() => handleEdit(item)}
                            onDelete={() => handleDelete(item)}
                          />
                       </td>
                    </tr>
                 ))}
              </tbody>
           </table>
         </div>
      </div>

      {showModal && (
        <Modal title={editingId ? "編輯公文函覆" : "新增公文函覆"} onClose={() => setShowModal(false)} width={700}>
           <div className="grid">
              <div className="field"><label className="label">主責部門 <span style={{color:'red'}}>*</span></label>
                 <select className="input" value={form.mainDepartment} onChange={e=>setForm({...form, mainDepartment:e.target.value})}>
                    {DEPARTMENT_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                 </select>
              </div>
              <div className="field"><label className="label">承辦人 <span style={{color:'red'}}>*</span></label>
                 <input className="input" value={form.handlerName} onChange={e=>setForm({...form, handlerName:e.target.value})} />
              </div>
              <div className="field"><label className="label">函覆內部編號 (自動)</label>
                 <input className="input" value={form.internalDocNo} readOnly style={{background:'rgba(255,255,255,0.05)', color:'#fbbf24'}} />
              </div>
              <div className="field"><label className="label">正式發文文號 (手動)</label>
                 <input className="input" value={form.officialDocNo} onChange={e=>setForm({...form, officialDocNo:e.target.value})} placeholder="請輸入正式發文文號" />
              </div>
              <div className="field"><label className="label">關聯來文 (內部編號)</label>
                 <select className="input" value={form.replyToInternalDocNo} onChange={e=>setForm({...form, replyToInternalDocNo:e.target.value})}>
                     <option value="">無</option>
                     {inbounds.map(i => <option key={i.id} value={i.internalDocNo || i.docNo}>{i.internalDocNo || i.docNo} - {i.subject}</option>)}
                 </select>
              </div>
              <DateField label="函覆日期" value={parseDate(form.date)} onChange={d=>setForm({...form, date:formatDate(d)})} />
              <div className="field"><label className="label">受文機關 <span style={{color:'red'}}>*</span></label><input className="input" value={form.agency} onChange={e=>setForm({...form, agency:e.target.value})} /></div>
              <div className="field" style={{gridColumn:'1/-1'}}><label className="label">主旨 <span style={{color:'red'}}>*</span></label><input className="input" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} /></div>
              <div className="field" style={{gridColumn:'1/-1'}}><label className="label">內容摘要</label><textarea className="input" rows={4} value={form.desc} onChange={e=>setForm({...form, desc:e.target.value})} /></div>
           </div>
           <FileUploadKit existingFiles={[]} onFilesSelected={fs => setFiles(p => [...p, ...fs])} />
           <div className="row" style={{marginTop:20, justifyContent:'flex-end', gap:10}}>
              <button className="btn" style={{width:'auto', background:'transparent', border:'1px solid #555'}} onClick={() => handleSave(false)} disabled={isSubmitting}>僅儲存</button>
              <button className="btn" style={{width:'auto'}} onClick={() => handleSave(true)} disabled={isSubmitting}>送出法務審查</button>
           </div>
        </Modal>
      )}

      {selectedReq && (
        <Modal title={`公文詳情: ${selectedReq.title}`} onClose={() => setSelectedReq(null)} width={900}>
           <div style={{display:'grid', gridTemplateColumns:'1fr 350px', gap:24}}>
              <div>
                 <div className="grid">
                    <div className="field"><label className="label">編號</label><div className="input" style={{border:'none'}}>{selectedReq.docCode}</div></div>
                    <div className="field"><label className="label">機關</label><div className="input" style={{border:'none'}}>{selectedReq.counterparty}</div></div>
                    <div className="field"><label className="label">部門/承辦</label><div className="input" style={{border:'none'}}>{(selectedReq as any).mainDepartment} / {(selectedReq as any).handlerName}</div></div>
                    <div className="field" style={{gridColumn:'1/-1'}}><label className="label">摘要</label>
                       <div style={{background:'rgba(255,255,255,0.05)', padding:10, borderRadius:6, whiteSpace:'pre-wrap'}}>{selectedReq.description}</div>
                    </div>
                 </div>
                 <div style={{marginTop:20}}>
                    <label className="label">附件</label>
                    <FileUploadKit existingFiles={selectedReq.attachments || []} disabled />
                 </div>
              </div>
              <div>
                 {flowLoading ? <div>Loading flow...</div> : (
                    <ApprovalFlowPanel 
                        flow={flow} 
                        onDecision={handleDecision} 
                        requestId={selectedReq.id}
                        reviewAttachments={selectedReq.reviewAttachments}
                        kcAttachments={selectedReq.kcAttachments}
                        onReviewAttachmentsChange={(files) => setSelectedReq(prev => prev ? ({...prev, reviewAttachments: files}) : null)}
                        onKcAttachmentsChange={(files) => setSelectedReq(prev => prev ? ({...prev, kcAttachments: files}) : null)}
                    />
                 )}
              </div>
           </div>
           <div className="row" style={{marginTop:20, justifyContent:'flex-end'}}>
              <button className="btn" style={{width:'auto'}} onClick={() => setSelectedReq(null)}>關閉</button>
           </div>
        </Modal>
      )}
    </div>
  );
}